import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Toolbar from '@material-ui/core/Toolbar';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MailIcon from '@material-ui/icons/Mail';
import DashboardIcon from '@material-ui/icons/Dashboard';
import SettingsIcon from '@material-ui/icons/Settings';
import WatchIcon from '@material-ui/icons/Watch';
import LinkIcon from '@material-ui/icons/Link';
import HistoryIcon from '@material-ui/icons/History';
import GroupIcon from '@material-ui/icons/Group';
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import ContactsIcon from '@material-ui/icons/Contacts';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import AddCircleIcon from '@material-ui/icons/AddCircle';

import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import { useDispatch, useSelector } from 'react-redux';
import logo from 'url:~/src/assets/neuralert_logo_inverted.png';
import { MENU_WIDTH } from '../constants';
import { logout, getCurrentUser } from '../store/actions/auth';
import { version } from '../../package.json';

const Menu = () => {
  const useStyles = makeStyles((theme) => ({
    root: {
      display: 'flex',
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: '#1e3f9e',
    },
    drawer: {
      width: MENU_WIDTH,
      flexShrink: 0,
    },
    drawerPaper: {
      width: MENU_WIDTH,
      backgroundColor: '#fafafa',
    },
    drawerContainer: {
      overflow: 'auto',
      marginTop: '2rem',
      height: 'calc(100vh - 100px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    },
    icon: {
      color: '#1e3f9e',
    },
    activeIcon: {
      color: '#00d1b2',
    },
    active: {
      backgroundColor: '#eee',
    },
    logo: {
      maxHeight: 100,
      width: 'auto',
      height: 'auto',
      paddingBottom: 20,
      paddingTop: 5,
    },
    build: {
      margin: '8px 16px',
    },
  }));
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.admin.currentUserInfo);

  useEffect(() => {
    dispatch(getCurrentUser());
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    history.push(`/`);
  };

  const handleMenuClick = (menuItem) => {
    if (menuItem.action) {
      menuItem.action();
    }
    if (menuItem.link) {
      history.push(menuItem.link);
    }
  };

  const getIcon = (iconType) => {
    switch (iconType) {
      case 'dashboard': {
        return <DashboardIcon />;
      }
      case 'watch': {
        return <WatchIcon />;
      }
      case 'notification': {
        return <NotificationImportantIcon />;
      }
      case 'admin_panel_settings': {
        return <ContactsIcon />;
      }
      case 'account_box': {
        return <AccountBoxIcon />;
      }
      case 'settings': {
        return <SettingsIcon />;
      }
      case 'exittoapp': {
        return <ExitToAppIcon />;
      }
      case 'hospital': {
        return <LocalHospitalIcon />;
      }
      case 'add_circle': {
        return <AddCircleIcon />;
      }
      case 'link': {
        return <LinkIcon />;
      }
      case 'group': {
        return <GroupIcon />;
      }
      case 'history': {
        return <HistoryIcon />;
      }
      default: {
        return <MailIcon />;
      }
    }
  };

  const classes = useStyles();

  const topMenuItems = [
    {
      label: 'Patients',
      icon: 'hospital',
      link: `/patients`,
    },
    {
      label: 'Monitors',
      icon: 'watch',
      link: `/monitors`,
    },
    {
      label: 'Users',
      icon: 'admin_panel_settings',
      link: `/admin`,
    },
    {
      label: 'Groups',
      icon: 'group',
      link: '/groups',
    },
  ];
  const bottomMenuItems = [
    {
      label: 'History',
      icon: 'history',
      link: '/history',
    },
    {
      label: 'Add Monitors',
      icon: 'add_circle',
      link: `/add-monitor`,
    },
    {
      label: 'Link Monitor',
      icon: 'link',
      link: `/link`,
    },
    {
      label: 'My Profile',
      icon: 'account_box',
      link: `/profile`,
    },
    {
      label: 'Logout',
      icon: 'exittoapp',
      action: handleLogout,
    },
  ];

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <img className={classes.logo} src={logo} alt="logo" />
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <Toolbar />
        <div className={classes.drawerContainer}>
          <div>
            <List>
              {topMenuItems.map((menuItem) => {
                const active = menuItem.link && useRouteMatch(menuItem.link);
                return (
                  <ListItem
                    onClick={() => {
                      handleMenuClick(menuItem);
                    }}
                    button
                    key={menuItem.label}
                    className={active ? classes.active : null}
                  >
                    <ListItemIcon className={active ? classes.activeIcon : classes.icon}>
                      {getIcon(menuItem.icon)}
                    </ListItemIcon>
                    <ListItemText primary={menuItem.label} />
                  </ListItem>
                );
              })}
            </List>
            <Divider />
            <List>
              {bottomMenuItems
                .filter((menuItem) => {
                  const role = currentUser.role?.name;
                  if (
                    (menuItem.label === 'Add Monitors' 
                    // || menuItem.label === 'History'
                    ) &&
                    (!role || role === 'HCP' || role === 'Supervisor')
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((menuItem) => {
                  const active = menuItem.link && useRouteMatch(menuItem.link);
                  return (
                    <ListItem
                      onClick={() => {
                        handleMenuClick(menuItem);
                      }}
                      button
                      key={menuItem.label}
                      className={active ? classes.active : null}
                    >
                      <ListItemIcon className={active ? classes.activeIcon : classes.icon}>
                        {getIcon(menuItem.icon)}
                      </ListItemIcon>
                      <ListItemText primary={menuItem.label} />
                    </ListItem>
                  );
                })}
            </List>
          </div>
          <div className={classes.build}>{`Build Version: ${version}`}</div>
        </div>
      </Drawer>
    </div>
  );
};

export default Menu;
